import { create } from 'zustand';
import {
  Conversation,
  ChatMessage,
  ConversationStatus,
  ConversationStats,
  ConversationQueryDto,
  chatService,
} from '@/services/chat.service';

interface ChatState {
  // State
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: ChatMessage[];
  stats: ConversationStats | null;
  isLoading: boolean;
  isLoadingMessages: boolean;
  isSending: boolean;
  hasMoreMessages: boolean;
  oldestMessageId: string | null;

  // Filters
  filters: ConversationQueryDto;

  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Actions
  setFilters: (filters: Partial<ConversationQueryDto>) => void;
  clearFilters: () => void;

  // Conversation Actions
  fetchConversations: (params?: ConversationQueryDto) => Promise<void>;
  fetchStats: () => Promise<void>;
  selectConversation: (conversation: Conversation | null) => void;
  claimConversation: (conversationId: string) => Promise<void>;
  releaseConversation: (conversationId: string) => Promise<void>;
  closeConversation: (conversationId: string, reason?: string) => Promise<void>;
  reopenConversation: (conversationId: string) => Promise<void>;

  // Message Actions
  fetchMessages: (conversationId: string, loadMore?: boolean) => Promise<void>;
  sendMessage: (conversationId: string, content: string, mediaUrl?: string, mediaType?: string, mediaFileName?: string) => Promise<void>;
  clearMessages: () => void;

  // Real-time updates
  addMessage: (message: ChatMessage) => void;
  updateMessageStatus: (messageId: string, status: string) => void;
  updateConversation: (conversationId: string, updates: Partial<Conversation>) => void;
  updateConversationLastMessage: (conversationId: string, lastMessage: any, unreadCount?: number) => void;
  incrementUnread: (conversationId: string) => void;
  markAsRead: (conversationId: string) => void;
}

const defaultFilters: ConversationQueryDto = {
  page: 1,
  limit: 20,
};

export const useChatStore = create<ChatState>()((set, get) => ({
  // Initial State
  conversations: [],
  selectedConversation: null,
  messages: [],
  stats: null,
  isLoading: false,
  isLoadingMessages: false,
  isSending: false,
  hasMoreMessages: false,
  oldestMessageId: null,
  filters: defaultFilters,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },

  // Filter Actions
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters, page: 1 },
    }));
    get().fetchConversations();
  },

  clearFilters: () => {
    set({ filters: defaultFilters });
    get().fetchConversations();
  },

  // Conversation Actions
  fetchConversations: async (params) => {
    set({ isLoading: true });
    try {
      const filters = params || get().filters;
      const response = await chatService.getConversations(filters);
      set({
        conversations: response.conversations,
        pagination: response.pagination,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching conversations:', error);
      set({ isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await chatService.getConversationStats();
      set({ stats });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  },

  selectConversation: (conversation) => {
    set({ selectedConversation: conversation, messages: [], hasMoreMessages: false, oldestMessageId: null });
    if (conversation) {
      get().fetchMessages(conversation.id);
      // Mark as read when selecting
      if (conversation.unreadCount > 0) {
        get().markAsRead(conversation.id);
      }
    }
  },

  claimConversation: async (conversationId) => {
    try {
      const updated = await chatService.claimConversation(conversationId);
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, ...updated } : c
        ),
        selectedConversation:
          state.selectedConversation?.id === conversationId
            ? { ...state.selectedConversation, ...updated }
            : state.selectedConversation,
      }));
    } catch (error) {
      console.error('Error claiming conversation:', error);
      throw error;
    }
  },

  releaseConversation: async (conversationId) => {
    try {
      const updated = await chatService.releaseConversation(conversationId);
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, ...updated } : c
        ),
        selectedConversation:
          state.selectedConversation?.id === conversationId
            ? { ...state.selectedConversation, ...updated }
            : state.selectedConversation,
      }));
    } catch (error) {
      console.error('Error releasing conversation:', error);
      throw error;
    }
  },

  closeConversation: async (conversationId, reason) => {
    try {
      const updated = await chatService.closeConversation(conversationId, reason);
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, ...updated } : c
        ),
        selectedConversation:
          state.selectedConversation?.id === conversationId
            ? { ...state.selectedConversation, ...updated }
            : state.selectedConversation,
      }));
    } catch (error) {
      console.error('Error closing conversation:', error);
      throw error;
    }
  },

  reopenConversation: async (conversationId) => {
    try {
      const updated = await chatService.reopenConversation(conversationId);
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, ...updated } : c
        ),
        selectedConversation:
          state.selectedConversation?.id === conversationId
            ? { ...state.selectedConversation, ...updated }
            : state.selectedConversation,
      }));
    } catch (error) {
      console.error('Error reopening conversation:', error);
      throw error;
    }
  },

  // Message Actions
  fetchMessages: async (conversationId, loadMore = false) => {
    set({ isLoadingMessages: true });
    try {
      const params: any = { limit: 50 };
      if (loadMore && get().oldestMessageId) {
        params.before = get().oldestMessageId;
      }

      const response = await chatService.getMessages(conversationId, params);

      set((state) => ({
        messages: loadMore
          ? [...response.messages, ...state.messages]
          : response.messages,
        hasMoreMessages: response.hasMore,
        oldestMessageId: response.oldestId,
        isLoadingMessages: false,
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
      set({ isLoadingMessages: false });
    }
  },

  sendMessage: async (conversationId, content, mediaUrl, mediaType, mediaFileName) => {
    set({ isSending: true });
    try {
      const message = await chatService.sendMessage(conversationId, {
        content,
        mediaUrl,
        mediaType,
        mediaFileName,
      });

      // Add message to local state
      set((state) => ({
        messages: [...state.messages, message],
        isSending: false,
      }));
    } catch (error) {
      console.error('Error sending message:', error);
      set({ isSending: false });
      throw error;
    }
  },

  clearMessages: () => {
    set({ messages: [], hasMoreMessages: false, oldestMessageId: null });
  },

  // Real-time updates
  addMessage: (message) => {
    const state = get();

    // Only add if it's for the currently selected conversation
    if (state.selectedConversation?.id === message.conversationId) {
      // Check if message already exists
      const exists = state.messages.some((m) => m.id === message.id);
      if (!exists) {
        set((state) => ({
          messages: [...state.messages, message],
        }));
      }
    }
  },

  updateMessageStatus: (messageId, status) => {
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, status: status as any } : m
      ),
    }));
  },

  updateConversation: (conversationId, updates) => {
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId ? { ...c, ...updates } : c
      ),
      selectedConversation:
        state.selectedConversation?.id === conversationId
          ? { ...state.selectedConversation, ...updates }
          : state.selectedConversation,
    }));
  },

  updateConversationLastMessage: (conversationId, lastMessage, unreadCount) => {
    set((state) => {
      const conversations = state.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              lastMessage,
              lastMessageAt: lastMessage.createdAt,
              unreadCount: unreadCount ?? c.unreadCount,
            }
          : c
      );

      // Sort by lastMessageAt
      conversations.sort((a, b) => {
        const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return dateB - dateA;
      });

      return { conversations };
    });
  },

  incrementUnread: (conversationId) => {
    const state = get();
    // Don't increment if conversation is selected
    if (state.selectedConversation?.id === conversationId) return;

    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: c.unreadCount + 1 } : c
      ),
    }));
  },

  markAsRead: async (conversationId) => {
    try {
      await chatService.markAsRead(conversationId);
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c
        ),
        selectedConversation:
          state.selectedConversation?.id === conversationId
            ? { ...state.selectedConversation, unreadCount: 0 }
            : state.selectedConversation,
      }));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  },
}));
